import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject, from, throwError, timer } from 'rxjs';
import { map, catchError, switchMap, retry, retryWhen, delay, takeUntil } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import {
  PubSubMessage,
  PubSubMessageData,
  ApplicationMessage,
  MessageType,
  TopicConfig,
  SubscriptionConfig,
  PublishResult,
  ReceiveResult,
  PubSubStats,
  PubSubConfig
} from '../../models/pubsub/pubsub-message.model';
import { getFirebaseConfigInfo } from '../../../../environments/firebase.config';
import { DomandaSostegnoPubblicataMessage } from '../../models/pubsub/domanda-sostegno-pubblicata.model';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GooglePubSubService implements OnDestroy {
  private isConnected = false;
  private connectionStatus$ = new BehaviorSubject<boolean>(false);
  private messageSubject$ = new Subject<PubSubMessageData>();
  private destroy$ = new Subject<void>();

  // Configurazione
  private config: PubSubConfig;
  private topics: Map<string, any> = new Map();
  private subscriptions: Map<string, any> = new Map();

  // Statistiche
  private stats: Map<string, PubSubStats> = new Map();

  // Mock per il browser
  private messageQueue: PubSubMessageData[] = [];
  private messageHandlers: Map<string, (message: PubSubMessageData) => void> = new Map();

  constructor(private logger: NGXLogger) {
    this.config = this.initializeConfig();
    this.initializeConnection();
  }

  // Normalizza il nome del topic rimuovendo il prefisso completo
  private normalizeTopic(topicName: string): string {
    return topicName.replace(/^projects\/[\w-]+\/topics\//, '');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  /**
   * Inizializza la configurazione Pub/Sub
   */
  private initializeConfig(): PubSubConfig {
    const firebaseConfig = getFirebaseConfigInfo();

    return {
      projectId: firebaseConfig.projectId,
      // In produzione, usa le credenziali del servizio
      // keyFilename: 'path/to/service-account-key.json',
      // oppure
      // credentials: { client_email: '...', private_key: '...' }
    };
  }

  /**
   * Inizializza la connessione a Pub/Sub (Mock per browser)
   */
  private async initializeConnection(): Promise<void> {
    try {
     // this.logger.info('[PUBSUB] Inizializzazione connessione Google Pub/Sub (Mock)...');

      // Simula connessione
      await new Promise(resolve => setTimeout(resolve, 100));

      this.isConnected = true;
      this.connectionStatus$.next(true);

    /*   this.logger.info('[PUBSUB] ✅ Connessione Pub/Sub Mock stabilita', {
        projectId: this.config.projectId,
        mode: 'browser-mock'
      });
 */
    } catch (error) {
      this.logger.error('[PUBSUB] ❌ Errore connessione Pub/Sub Mock:', error);
      this.isConnected = false;
      this.connectionStatus$.next(false);
    }
  }

  /**
   * Verifica se il servizio è connesso
   */
  isServiceConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Ottiene lo stato della connessione come Observable
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Pubblica un messaggio su un topic (Mock per browser)
   */
  async publishMessage(
    topicName: string,
    message: any,
    attributes?: { [key: string]: string }
  ): Promise<PublishResult> {
    if (!this.isConnected) {
      throw new Error('Pub/Sub non connesso');
    }

    try {
      const topicKey = this.normalizeTopic(topicName);
      this.logger.info('[PUBSUB] Pubblicazione messaggio @ topic: ',topicKey);

      // Simula pubblicazione
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Crea il messaggio mock
       const messageData: PubSubMessageData = {
        messageId,
        topic: topicKey,
        subscription: `${topicKey}-sub`,
        data: message,
        attributes: {
          messageType: message?.type,
          timestamp: new Date().toISOString(),
          ...(attributes || {})
        },
        publishTime: new Date(),
        deliveryAttempt: 1
      };

      // Aggiungi alla coda
      this.messageQueue.push(messageData);

      // Emetti il messaggio
      this.messageSubject$.next(messageData);

      // Chiama i gestori registrati
      const handler = this.messageHandlers.get(topicKey);
      if (handler) {
        handler(messageData);
      }

      const result: PublishResult = {
        messageId: messageData.messageId,
        success: true,
        timestamp: new Date()
      };

      this.logger.info('[PUBSUB] ✅ Messaggio pubblicato con successo:', result);
      return result;

    } catch (error: any) {
      this.logger.error('[PUBSUB] ❌ Errore pubblicazione messaggio:', error);

      const result: PublishResult = {
            messageId: '0',
        success: false,
        error: error.message,
        timestamp: new Date()
      };

      return result;
    }
  }

  /**
   * Sottoscrive a un topic e riceve messaggi (Mock per browser)
   */
  subscribeToTopic(
    topicName: string,
    subscriptionName: string,
    messageHandler: (message: PubSubMessageData) => void
  ): Observable<ReceiveResult> {
    if (!this.isConnected) {
      return throwError(() => new Error('Pub/Sub non connesso'));
    }

    // Registra il gestore per questo topic (normalizzato)
    const topicKey = this.normalizeTopic(topicName);
    this.messageHandlers.set(topicKey, messageHandler);

    return new Observable(observer => {
      // Simula sottoscrizione
      this.logger.info('[PUBSUB] Sottoscrizione Mock a topic:', topicKey);

      // Restituisci un observable che emette i messaggi esistenti
      const existingMessages = this.messageQueue.filter(msg => msg.topic === topicKey);

      existingMessages.forEach(messageData => {
        try {
          // Chiama il gestore personalizzato
          messageHandler(messageData);

          const result: ReceiveResult = {
            message: messageData,
            ackId: messageData.messageId,
            success: true,
            timestamp: new Date()
          };

          observer.next(result);
        } catch (error: any) {
          this.logger.error('[PUBSUB] ❌ Errore gestione messaggio (Mock):', error);

          const result: ReceiveResult = {
            message: messageData,
            ackId: messageData.messageId,
            success: false,
            error: error.message,
            timestamp: new Date()
          };

          observer.next(result);
        }
      });

      // Cleanup quando l'observable viene completato
      return () => {
        this.messageHandlers.delete(topicKey);
        this.logger.info('[PUBSUB] Sottoscrizione Mock rimossa per topic:', topicKey);
      };
    });
  }

  /**
   * Crea un topic (Mock per browser)
   */
  async createTopic(topicConfig: TopicConfig): Promise<boolean> {
    if (!this.isConnected) {
      throw new Error('Pub/Sub non connesso');
    }

    try {
      this.logger.info('[PUBSUB] Creazione topic (Mock):', topicConfig.name);

      // Simula creazione topic
      await new Promise(resolve => setTimeout(resolve, 50));

      this.topics.set(topicConfig.name, { name: topicConfig.name });
      this.logger.info('[PUBSUB] ✅ Topic creato/verificato (Mock):', topicConfig.name);
      return true;

    } catch (error) {
      this.logger.error('[PUBSUB] ❌ Errore creazione topic (Mock):', error);
      return false;
    }
  }

  /**
   * Crea una subscription (Mock per browser)
   */
  async createSubscription(subscriptionConfig: SubscriptionConfig): Promise<boolean> {
    if (!this.isConnected) {
      throw new Error('Pub/Sub non connesso');
    }

    try {
      this.logger.info('[PUBSUB] Creazione subscription (Mock):', subscriptionConfig.name);

      // Simula creazione subscription
      await new Promise(resolve => setTimeout(resolve, 50));

      this.subscriptions.set(subscriptionConfig.name, { name: subscriptionConfig.name });
      this.logger.info('[PUBSUB] ✅ Subscription creata/verificata (Mock):', subscriptionConfig.name);
      return true;

    } catch (error) {
      this.logger.error('[PUBSUB] ❌ Errore creazione subscription (Mock):', error);
      return false;
    }
  }

  /**
   * Ottiene le statistiche per un topic (Mock per browser)
   */
  async getTopicStats(topicName: string): Promise<PubSubStats | null> {
    if (!this.isConnected) {
      return null;
    }

    try {
      const topicKey = this.normalizeTopic(topicName);
      // Simula recupero statistiche
      await new Promise(resolve => setTimeout(resolve, 50));

      const messageCount = this.messageQueue.filter(msg => msg.topic === topicKey).length;
      const byteCount = this.messageQueue
        .filter(msg => msg.topic === topicKey)
        .reduce((total, msg) => total + JSON.stringify(msg.data).length, 0);

      const stats: PubSubStats = {
        topic: topicKey,
        messageCount,
        byteCount,
        lastUpdated: new Date()
      };

      this.stats.set(topicKey, stats);
      return stats;

    } catch (error) {
      this.logger.error('[PUBSUB] ❌ Errore recupero statistiche topic (Mock):', error);
      return null;
    }
  }

  /**
   * Ottiene tutti i messaggi come Observable
   */
  getMessages(): Observable<PubSubMessageData> {
    return this.messageSubject$.asObservable();
  }

  /**
   * Filtra i messaggi per tipo
   */
  getMessagesByType(messageType: MessageType): Observable<PubSubMessageData> {
    return this.messageSubject$.pipe(
      map(message => message.data.type === messageType ? message : null),
      map(message => message!)
    );
  }

  /**
   * Pubblica un messaggio di fascicolo
   */
  async publishDomandaSostegnoPubblicataMessage(domandaSostegnoPubblicataMessage: DomandaSostegnoPubblicataMessage): Promise<PublishResult> {
    const appMessage = {
      type: 'domanda_sostegno_pubblicata',
      ...domandaSostegnoPubblicataMessage
    } as any;

    return this.publishMessage(environment.pubsub.domanda_sostegno_pubblicata, appMessage);
  }


  /**
   * Pubblica un messaggio di fascicolo
   */
  async publishFascicoloMessage(
    type: MessageType.FASCICOLO_CREATED | MessageType.FASCICOLO_UPDATED | MessageType.FASCICOLO_DELETED,
    fascicoloId: string,
    beneficiario: { nome: string; cognome: string; codiceFiscale: string },
    userId?: string,
    metadata?: { [key: string]: any }
  ): Promise<PublishResult> {
    const message = {
      type,
      fascicoloId,
      beneficiario,
      timestamp: new Date(),
      userId,
      metadata
    } as any;

    return this.publishMessage('fascicoli', message);
  }

  /**
   * Pubblica un messaggio di wizard
   */
  async publishWizardMessage(
    type: MessageType.WIZARD_STARTED | MessageType.WIZARD_COMPLETED | MessageType.WIZARD_FAILED,
    wizardId: string,
    step: number,
    totalSteps: number,
    progress: number,
    fascicoloId?: string,
    userId?: string,
    error?: string,
    metadata?: { [key: string]: any }
  ): Promise<PublishResult> {
    const message = {
      type,
      wizardId,
      fascicoloId,
      step,
      totalSteps,
      progress,
      timestamp: new Date(),
      userId,
      error,
      metadata
    } as any;

    return this.publishMessage('wizards', message);
  }

  /**
   * Pubblica un messaggio di file
   */
  async publishFileMessage(
    type: MessageType.FILE_UPLOADED | MessageType.FILE_PROCESSED,
    fileId: string,
    fileName: string,
    fileSize: number,
    fileType: string,
    fascicoloId?: string,
    wizardId?: string,
    userId?: string,
    processingResult?: any,
    metadata?: { [key: string]: any }
  ): Promise<PublishResult> {
    const message = {
      type,
      fileId,
      fileName,
      fileSize,
      fileType,
      fascicoloId,
      wizardId,
      timestamp: new Date(),
      userId,
      processingResult,
      metadata
    } as any;

    return this.publishMessage('files', message);
  }

  /**
   * Pubblica una notifica
   */
  async publishNotification(
    notificationId: string,
    title: string,
    message: string,
    priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium',
    category: 'info' | 'warning' | 'error' | 'success' = 'info',
    userId?: string,
    expiresAt?: Date,
    metadata?: { [key: string]: any }
  ): Promise<PublishResult> {
    const notificationMessage = {
      type: MessageType.NOTIFICATION,
      notificationId,
      title,
      message,
      priority,
      category,
      userId,
      timestamp: new Date(),
      expiresAt,
      metadata
    } as any;

    return this.publishMessage('notifications', notificationMessage);
  }

  /**
   * Pubblica un alert di sistema
   */
  async publishSystemAlert(
    alertId: string,
    severity: 'info' | 'warning' | 'error' | 'critical',
    component: string,
    message: string,
    resolved: boolean = false,
    metadata?: { [key: string]: any }
  ): Promise<PublishResult> {
    const alertMessage = {
      type: MessageType.SYSTEM_ALERT,
      alertId,
      severity,
      component,
      message,
      timestamp: new Date(),
      resolved,
      metadata
    } as any;

    return this.publishMessage('system-alerts', alertMessage);
  }

  /**
   * Disconnette dal servizio Pub/Sub (Mock per browser)
   */
  disconnect(): void {
    this.isConnected = false;
    this.connectionStatus$.next(false);
    this.messageHandlers.clear();
    this.messageQueue = [];
    this.logger.info('[PUBSUB] Disconnesso da Google Pub/Sub (Mock)');
  }

  /**
   * Riconnette al servizio Pub/Sub
   */
  async reconnect(): Promise<void> {
    this.disconnect();
    await this.initializeConnection();
  }
}
